import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}
interface State {
  error: Error | null;
}

/**
 * App-level React error boundary. A render-time throw anywhere below it would
 * otherwise blank the whole SPA; here it becomes a recoverable "something went
 * wrong" screen with a reload. Intentionally self-contained (no hooks, no
 * @app/ui, no i18n context) so it still renders even if a provider is what
 * broke; it shows both TR and EN since the locale context may be unavailable.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surfaced in the console for local debugging; wire to telemetry if added.
    console.error("Unhandled render error:", error, info.componentStack);
  }

  private reload = () => {
    this.setState({ error: null });
    window.location.reload();
  };

  override render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <div
        role="alert"
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "1rem",
          padding: "2rem",
          textAlign: "center",
          fontFamily: "system-ui, sans-serif",
        }}
      >
        <h1 style={{ fontSize: "1.25rem", fontWeight: 600, margin: 0 }}>
          Bir şeyler ters gitti · Something went wrong
        </h1>
        <p style={{ maxWidth: "28rem", color: "#666", margin: 0 }}>
          Sayfa beklenmedik bir hatayla karşılaştı. Lütfen sayfayı yenileyin.
          <br />
          The page hit an unexpected error. Please reload.
        </p>
        {/* Self-contained fallback: no @app/ui so it renders even if a provider broke. */}
        {/* eslint-disable-next-line no-restricted-syntax */}
        <button
          type="button"
          onClick={this.reload}
          style={{
            padding: "0.5rem 1.25rem",
            borderRadius: "0.5rem",
            border: "1px solid #ccc",
            background: "#115f44",
            color: "#fff",
            cursor: "pointer",
            fontSize: "0.9rem",
          }}
        >
          Sayfayı yenile · Reload
        </button>
      </div>
    );
  }
}
